import { useEffect, useState } from 'react'
import type { Church, OccurrenceWithEvent, SlideshowAdvanceMode } from '@fellowship/shared'
import { SlideshowSlide } from './BulletinTemplates'

export interface SlideshowPlayerProps {
  church: Church
  occurrences: OccurrenceWithEvent[]
  eventQrMap: Record<string, string>
  showImage: boolean
  showDetails: boolean
  showQr: boolean
  advanceMode: SlideshowAdvanceMode
  intervalSeconds: number
  // Only cycles/responds to input while active — the sidebar preview renders
  // this same component at rest (slide 1, no timer) so editing settings
  // doesn't fight with an advancing slideshow behind the panel.
  active: boolean
}

export function SlideshowPlayer({
  church,
  occurrences,
  eventQrMap,
  showImage,
  showDetails,
  showQr,
  advanceMode,
  intervalSeconds,
  active,
}: SlideshowPlayerProps) {
  const [index, setIndex] = useState(0)

  // Reset to the first slide whenever the player goes inactive (e.g. exiting
  // full-screen) or the event list changes, so re-entering always starts clean.
  useEffect(() => {
    if (!active) setIndex(0)
  }, [active])
  useEffect(() => {
    setIndex(0)
  }, [occurrences.length])

  const advance = () => setIndex((i) => (occurrences.length === 0 ? 0 : (i + 1) % occurrences.length))
  const retreat = () => setIndex((i) => (occurrences.length === 0 ? 0 : (i - 1 + occurrences.length) % occurrences.length))

  useEffect(() => {
    if (!active || advanceMode !== 'auto' || occurrences.length < 2) return
    const t = setInterval(advance, Math.max(3, intervalSeconds) * 1000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, advanceMode, intervalSeconds, occurrences.length])

  useEffect(() => {
    if (!active || advanceMode !== 'click') return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') advance()
      else if (e.key === 'ArrowLeft') retreat()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, advanceMode, occurrences.length])

  const occurrence = occurrences[index] ?? null

  return (
    <div
      onClick={active && advanceMode === 'click' ? advance : undefined}
      style={{ cursor: active && advanceMode === 'click' ? 'pointer' : undefined }}
    >
      <SlideshowSlide
        church={church}
        occurrence={occurrence}
        qrDataUrl={occurrence ? (eventQrMap[occurrence.id] ?? null) : null}
        showImage={showImage}
        showDetails={showDetails}
        showQr={showQr}
        slideIndex={index}
        slideCount={occurrences.length}
      />
    </div>
  )
}
